import "perfect-scrollbar/css/perfect-scrollbar.css";

import { createStyles, makeStyles } from "@material-ui/core";
import { ChevronDownIcon, RelodeTheme } from "../../";
import classnames from "classnames";
import PerfectScrollbar from "perfect-scrollbar";
import * as React from "react";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                overflowY: "hidden",
                flexGrow: 1,
                alignSelf: "stretch",
                justifySelf: "stretch",
                display: "flex",
                flexDirection: "column",
                position: "relative",
            },
            scroller: {
                overflowY: "auto",
                flexGrow: 1,
                alignSelf: "stretch",
                justifySelf: "stretch",
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
            },
            more: {
                position: "absolute",
                bottom: 12,
                right: 0,
                opacity: 0.15,
                pointerEvents: "none",
                padding: "4px 4px 0 4px",
                color: theme.palette.common.white,
                backgroundColor: theme.palette.common.black,
            },
            hideScrollbar: {
                overflow: "hidden",
            },
        }),
    { name: "ScrollContainer" }
);
interface ScrollPosition {
    x: number;
    y: number;
}
interface ScrollState {
    x: number;
    y: number;
}
const ScrollContext = React.createContext<ScrollState>({ x: 0, y: 0 });

interface Props {
    children?: React.ReactNode;
    className?: string;
    scrollbarType?: "none" | "perfect" | "normal";
    showMoreIcon?: boolean;
    scrollContentKey?: string;
}

type ElementRef = HTMLElement | undefined;

export const ScrollContainer = React.memo((props: Props) => {
    const ps = React.useRef<PerfectScrollbar | null>(null);
    const scrollDiv = React.useRef<HTMLDivElement>(null);
    const lastScroll = React.useRef(0);
    const tick = React.useRef(false);
    const position = React.useRef<ScrollState>({ x: 0, y: 0 });
    const hasBeenScrolled = React.useRef(false);
    const [showMore, setShowMore] = React.useState(false);
    const [scrollPos, setScrollPos] = React.useState<ScrollState>({
        x: 0,
        y: 0,
    });
    const classes = useStyles({});
    const { children, className: classNameProp, showMoreIcon = true } = props;

    const rootClass = classnames(
        {
            [classes.root]: true,
        },
        classNameProp
    );

    const scrollerClass = React.useMemo(
        () =>
            classnames({
                [classes.scroller]: true,
                [classes.hideScrollbar]: props.scrollbarType === "none",
            }),
        [classes.hideScrollbar, classes.scroller, props.scrollbarType]
    );

    const iOS =
        !!navigator.platform && /iPad|iPhone|iPod/.test(navigator.platform);

    React.useEffect(() => {
        if (iOS || props.scrollbarType !== "perfect") return;
        ps.current = new PerfectScrollbar(scrollDiv.current, {
            suppressScrollX: true,
            suppressScrollY: false,
        });

        return () => {
            if (!!ps.current) {
                ps.current.destroy();
                ps.current = null;
            }
        };
    }, [iOS, props.scrollbarType]);

    React.useLayoutEffect(() => {
        if (iOS) return;

        const div = scrollDiv.current;
        if (!div) return;

        const canScroll = div.scrollHeight > div.clientHeight;
        setShowMore(canScroll);
        const doScroll = (e: Event) => {
            try {
                if (!e.target) return;
                const element = e.target as HTMLElement;

                if (!element.scrollTop) return;
                lastScroll.current = element.scrollTop;
                if (!tick.current) {
                    window.requestAnimationFrame(() => {
                        const canStillScroll =
                            div.scrollHeight > div.clientHeight;
                        if (
                            lastScroll.current + element.offsetHeight >=
                            element.scrollHeight - 4
                        ) {
                            setShowMore(false);
                        } else {
                            setShowMore(canStillScroll);
                        }
                        tick.current = false;
                    });
                    tick.current = true;
                }
            } catch (e) {
                // do nothing
            }
        };
        div.addEventListener("scroll", doScroll, { passive: true });
        return () => {
            div.removeEventListener("scroll", doScroll);
        };
    }, [iOS, showMoreIcon]);

    React.useLayoutEffect(() => {
        const _div = scrollDiv.current;
        let timer = undefined;
        const throttle = true;
        const generateNewScrollPosition = (e: Event) => {
            const element = e.target as HTMLElement;
            const handle = () => {
                const newPosition: ScrollState = {
                    y: Math.round(element.scrollTop),
                    x: Math.round(element.scrollLeft),
                };
                if (
                    position.current.x !== newPosition.x ||
                    position.current.y !== newPosition.x ||
                    position.current.y === 0 ||
                    position.current.x === 0
                ) {
                    position.current = newPosition;
                }
                setScrollPos(newPosition);
            };
            if (throttle && !timer) {
                timer = window.setTimeout(() => {
                    handle();
                    timer = undefined;
                }, 100);
            } else {
                handle();
            }
        };
        _div.addEventListener("scroll", generateNewScrollPosition, {
            passive: true,
        });
        return () => {
            _div.removeEventListener("scroll", generateNewScrollPosition);
            if (!!timer) timer = undefined;
        };
    }, []);

    React.useEffect(() => {
        if (!iOS && !!scrollDiv.current) {
            scrollDiv.current.scrollTop = 0;
        }
    }, [iOS, props.scrollContentKey]);

    return (
        <ScrollContext.Provider value={scrollPos}>
            <div className={rootClass}>
                <div className={scrollerClass} ref={scrollDiv}>
                    {children}
                </div>
                {showMoreIcon && showMore && (
                    <div className={classes.more}>
                        <ChevronDownIcon fontSize="default" />
                    </div>
                )}
            </div>
        </ScrollContext.Provider>
    );
});

export const useScrollerPosition = () => {
    const ctx = React.useContext(ScrollContext);
    if (!ctx) {
        throw new Error(
            "useScrollerPosition can only be used inside of a ScrollContainer"
        );
    }

    return ctx;
};
